'use client';

import Icon from '@/components/ui/AppIcon';
import DemoDataPill from './DemoDataPill';

const cardStyle = {
  background: '#FFFFFF',
  boxShadow: '0 2px 12px rgba(0,0,0,0.06)',
  border: '1px solid #F0F0F0',
};

export default function Row3GrowthTrajectory() {
  return (
    <section className="grid grid-cols-1 lg:grid-cols-3 gap-4">
      <BookingsTrend />
      <PipelineVelocity />
      <WinRateByLeadSource />
    </section>
  );
}

// ── 1. Bookings Trend ──────────────────────────────────────────────────────

const BOOKINGS_SERIES = {
  thisMonth: [] as number[],
  lastMonth: [] as number[],
  lastYearSame: [] as number[],
};
const BOOKINGS_MTD = 0; // $k
const BOOKINGS_DELTA_PCT = 0; // vs same period last month

function BookingsTrend() {
  const w = 280;
  const h = 110;
  const all = [
    ...BOOKINGS_SERIES.thisMonth,
    ...BOOKINGS_SERIES.lastMonth,
    ...BOOKINGS_SERIES.lastYearSame,
  ];
  // Guard: with no series data, avoid Math.max(...[]) = -Infinity.
  const max = all.length ? Math.max(...all) : 0;
  const min = all.length ? Math.min(...all) : 0;
  const range = max - min || 1;
  const xFor = (i: number, len: number) => (i / (len - 1)) * w;
  const yFor = (v: number) => h - ((v - min) / range) * (h - 6) - 3;
  const pathFor = (points: number[]) =>
    points.map((v, i) => `${i === 0 ? 'M' : 'L'} ${xFor(i, points.length)} ${yFor(v)}`).join(' ');

  return (
    <div className="relative rounded-xl p-5" style={cardStyle}>
      <DemoDataPill />
      <div className="flex items-center gap-2 mb-1">
        <Icon name="ArrowTrendingUpIcon" size={15} style={{ color: '#10B981' }} />
        <h3 className="text-base font-bold" style={{ color: '#000150', fontFamily: 'Inter, Poppins, sans-serif' }}>
          Bookings Trend
        </h3>
      </div>
      <p className="text-xs text-gray-400 mb-3">
        MTD ${BOOKINGS_MTD}k · <span style={{ color: '#10B981' }}>▲ {BOOKINGS_DELTA_PCT}%</span> vs same period last month
      </p>
      <svg width="100%" height={h} viewBox={`0 0 ${w} ${h}`} preserveAspectRatio="none" className="block">
        <path d={pathFor(BOOKINGS_SERIES.lastYearSame)} stroke="#9CA3AF" strokeWidth="1.25" strokeDasharray="3 3" fill="none" />
        <path d={pathFor(BOOKINGS_SERIES.lastMonth)} stroke="#07B0E9" strokeWidth="1.5" fill="none" strokeLinecap="round" />
        <path d={pathFor(BOOKINGS_SERIES.thisMonth)} stroke="#10B981" strokeWidth="2" fill="none" strokeLinecap="round" />
      </svg>
      <div className="flex items-center gap-3 mt-2 text-[11px]">
        <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full" style={{ background: '#10B981' }} /> This month</span>
        <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full" style={{ background: '#07B0E9' }} /> Last month</span>
        <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full" style={{ background: '#9CA3AF' }} /> Last yr</span>
      </div>
    </div>
  );
}

// ── 2. Pipeline Velocity ───────────────────────────────────────────────────

const PIPELINE = {
  totalDays: 0,
  deltaDays: 0, // negative = faster (good)
  leadToQuoted: 0,
  quotedToSigned: 0,
  signedToInstalled: 0,
};

function PipelineVelocity() {
  const seg = [
    { label: 'Lead → Quoted',  days: PIPELINE.leadToQuoted,      color: '#07B0E9' },
    { label: 'Quoted → Signed', days: PIPELINE.quotedToSigned,    color: '#033494' },
    { label: 'Signed → Installed', days: PIPELINE.signedToInstalled, color: '#10B981' },
  ];
  const total = seg.reduce((s, x) => s + x.days, 0);
  const faster = PIPELINE.deltaDays < 0;

  return (
    <div className="relative rounded-xl p-5" style={cardStyle}>
      <DemoDataPill />
      <div className="flex items-center gap-2 mb-1">
        <Icon name="ArrowsRightLeftIcon" size={15} style={{ color: '#033494' }} />
        <h3 className="text-base font-bold" style={{ color: '#000150', fontFamily: 'Inter, Poppins, sans-serif' }}>
          Pipeline Velocity
        </h3>
      </div>
      <p className="text-xs text-gray-400 mb-3">Avg days lead → contract</p>
      <div className="flex items-baseline gap-2 mb-3">
        <p
          className="text-4xl font-bold leading-none"
          style={{ color: '#000150', fontFamily: 'Inter, Poppins, sans-serif' }}
        >
          {PIPELINE.totalDays.toFixed(1)}d
        </p>
        <span
          className="text-xs font-bold"
          style={{ color: faster ? '#10B981' : '#EF4444' }}
        >
          {faster ? '▼' : '▲'} {Math.abs(PIPELINE.deltaDays).toFixed(1)}d vs last month
        </span>
      </div>
      <div className="flex h-3 rounded-full overflow-hidden mb-2.5" style={{ background: '#F3F4F6' }}>
        {seg.map((s) => (
          <div
            key={s.label}
            style={{ width: `${total > 0 ? (s.days / total) * 100 : 0}%`, background: s.color }}
            title={`${s.label}: ${s.days.toFixed(1)}d`}
          />
        ))}
      </div>
      <div className="space-y-1">
        {seg.map((s) => (
          <div key={s.label} className="flex items-center justify-between text-[11px]">
            <span className="flex items-center gap-1.5">
              <span className="w-2 h-2 rounded-full" style={{ background: s.color }} />
              <span className="text-gray-700">{s.label}</span>
            </span>
            <span className="font-semibold text-gray-600">{s.days.toFixed(1)}d</span>
          </div>
        ))}
      </div>
    </div>
  );
}

// ── 3. Win Rate by Lead Source ─────────────────────────────────────────────

const LEAD_SOURCES: { label: string; share: number; winRate: number; color: string }[] = [];

function WinRateByLeadSource() {
  return (
    <div className="relative rounded-xl p-5" style={cardStyle}>
      <DemoDataPill />
      <div className="flex items-center gap-2 mb-1">
        <Icon name="ChartPieIcon" size={15} style={{ color: '#8B5CF6' }} />
        <h3 className="text-base font-bold" style={{ color: '#000150', fontFamily: 'Inter, Poppins, sans-serif' }}>
          Win Rate by Lead Source
        </h3>
      </div>
      <p className="text-xs text-gray-400 mb-3">Bar width = share of pipeline · Label = win rate</p>
      <div className="flex h-7 rounded-lg overflow-hidden mb-3" style={{ background: '#F3F4F6' }}>
        {LEAD_SOURCES.map((s) => (
          <div
            key={s.label}
            className="flex items-center justify-center text-[10px] font-bold text-white"
            style={{ width: `${s.share}%`, background: s.color }}
            title={`${s.label}: ${s.share}% share · ${s.winRate}% win rate`}
          >
            {s.winRate}%
          </div>
        ))}
      </div>
      <div className="grid grid-cols-2 gap-x-2 gap-y-1">
        {LEAD_SOURCES.map((s) => (
          <div key={s.label} className="flex items-center gap-1.5 text-[11px]">
            <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ background: s.color }} />
            <span className="text-gray-700">{s.label}</span>
            <span className="ml-auto text-gray-500 font-medium">{s.share}% / {s.winRate}%</span>
          </div>
        ))}
      </div>
    </div>
  );
}
